import { useState } from 'react';

import styled from 'styled-components';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { WarehouseDeliveryTrelloView } from './WarehouseDeliveryTrelloView';
import { useSearchValueParam } from 'helpers/SearchParams/utils';
import { TotemInput } from 'components/TotemInput';
import { PrintWarehouseDeliveriesTags } from './components/PrintWarehouseDeliveriesTags';
import { useGetWarehouseDeliveriesQuery } from 'data/__generated__';
import { Loader } from 'components/Loader';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

export const WarehouseDeliveries = () => {
    const [selectedWarehouseDeliveryIds, setSelectedWarehouseDeliveryIds] = useState<string[]>([]);
    const { searchInputValue, searchValue, setSearchInputValue } = useSearchValueParam();
    const [deliverySearchDate, setDeliverySearchDate] = useState<Date | null>(null);

    const { data, loading, error } = useGetWarehouseDeliveriesQuery({
        variables: {
            searchDate: deliverySearchDate ? formatDateAsAnniversary({ dateTime: deliverySearchDate }) : null,
        },
    });

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération des livraisons');
    }

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.warehouseDeliveries} />
                </HeaderTitle>
                <Filters>
                    <SearchContainer>
                        <TotemInput
                            label="Choisir une livraison"
                            onChange={setSearchInputValue}
                            placeholder="Rechercher par ID ou site"
                            value={searchInputValue}
                        />
                    </SearchContainer>
                </Filters>
                <DateContainer>
                    <PrintWarehouseDeliveriesTags
                        warehouseDeliveries={
                            data?.warehouseDeliveries.filter((warehouseDelivery) =>
                                selectedWarehouseDeliveryIds.includes(warehouseDelivery._id),
                            ) || []
                        }
                    />
                    <TotemDatePicker
                        label="Date de livraison"
                        selected={deliverySearchDate}
                        onChange={(date) => {
                            setDeliverySearchDate(date as Date);
                        }}
                        isClearable
                    />
                </DateContainer>
            </Header>
            <Content>
                {loading || !data ? (
                    <Loader />
                ) : (
                    <WarehouseDeliveryTrelloView
                        warehouseDeliveries={data.warehouseDeliveries}
                        searchValue={searchValue}
                        selectedWarehouseDeliveryIds={selectedWarehouseDeliveryIds}
                        setSelectedWarehouseDeliveryIds={setSelectedWarehouseDeliveryIds}
                    />
                )}
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: hidden;
`;

const DateContainer = styled.div`
    display: flex;
    min-width: 300px;
    justify-self: end;

    & > :not(:first-child) {
        margin-left: 15px;
    }
`;

const Filters = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const SearchContainer = styled.div`
    width: 400px;
`;
